import { useState, useEffect } from "react";
import { API } from "../lib/api";
import { SectionHelp } from "./SectionHelp";

/* Email every family a personal link covering all their players.

   Used from two places, exactly like RosterPanel: the admin screen (club
   passcode, every team) and the coach view (team token, that one team). It
   takes its auth as a prop and, for a coach, a teamId that the API re-derives
   from the token anyway — the prop decides what to ASK for, the token decides
   what is ALLOWED.

   Shows who WOULD be emailed before sending anything — the send goes to real
   parents, so a dry run first is the difference between a mistake you catch and
   one the whole club sees.

   `autoFocus="remind"` runs the "who hasn't replied" dry run itself, for the
   "Remind families" shortcut on the coach dashboard — it still stops at the
   dry run, same as clicking the button by hand. It never auto-sends. */
export function FamilyInvitePanel({ adminCode, auth, teamId, teamName, autoFocus }) {
  const [families, setFamilies] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [pending, setPending] = useState(null);
  const [note, setNote] = useState("");
  // Families and staff get different messages — one asks about your player's
  // dates, the other about your own — so the send is picked, never merged.
  const [audience, setAudience] = useState("families");
  const isStaff = audience === "staff";
  const who = isStaff ? "coaches & staff" : "families";

  // `adminCode` stays supported so the admin screen's call site is unchanged.
  const credential = auth || (adminCode ? { adminCode } : {});
  const scopeLabel = teamName ? teamName : "the club";

  function authHeaders() {
    return credential.adminCode
      ? { "x-admin-code": credential.adminCode }
      : { "x-auth-token": credential.token };
  }
  // A coach must name their team on every call; the API validates the token
  // against that id, so naming someone else's team just fails to authenticate.
  const scopeQuery = teamId ? `teamId=${encodeURIComponent(teamId)}&` : "";
  const withScope = (body) => (teamId ? { ...body, teamId } : body);

  async function callPath(path, method, body) {
    const res = await fetch(`${API}${path}`, {
      method,
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: body ? JSON.stringify(withScope(body)) : undefined,
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new Error((data && data.error) || "Request failed");
    return data;
  }

  async function checkPending() {
    setError("");
    setResult(null);
    setBusy(true);
    try {
      setPending(await callPath(`/availability-remind?${scopeQuery}audience=${audience}`, "GET"));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  // Runs the dry run once, on mount, when opened from the "Remind families"
  // shortcut. `audience` is always "families" at mount so this can't fire the
  // staff dry run by accident.
  useEffect(() => {
    if (autoFocus === "remind") checkPending();
  }, []); // eslint-disable-line

  async function sendReminders() {
    if (!window.confirm(`Send a reminder to ${pending.families.length} ${who} in ${scopeLabel} who haven't replied?`)) return;
    setError("");
    setBusy(true);
    try {
      setResult(await callPath("/availability-remind", "POST", { note, audience }));
      setNote("");
      await checkPending();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function call(method, body) {
    const qs = method === "GET" ? `?${scopeQuery}audience=${audience}` : "";
    const res = await fetch(`${API}/family-invite${qs}`, {
      method,
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: body ? JSON.stringify(withScope({ ...body, audience })) : undefined,
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new Error((data && data.error) || "Request failed");
    return data;
  }

  // Switching audience clears everything on screen: a list of families still
  // showing while "Coaches & staff" is selected is exactly how you send the
  // wrong message to the wrong people.
  function switchAudience(next) {
    if (next === audience) return;
    setAudience(next);
    setFamilies(null);
    setPending(null);
    setResult(null);
    setError("");
  }

  async function preview() {
    setError("");
    setResult(null);
    setBusy(true);
    try {
      setFamilies((await call("GET")).families || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function sendAll() {
    if (!window.confirm(`Email a link to all ${families.length} ${who} in ${scopeLabel}?`)) return;
    setError("");
    setBusy(true);
    try {
      setResult(await call("POST", {}));
      await preview();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function sendOne(email) {
    setError("");
    setBusy(true);
    try {
      setResult(await call("POST", { email }));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
      <h3 className="font-bold text-gray-800">
        Email {isStaff ? "coaches & staff" : "parents"} their link
      </h3>
      <SectionHelp label="When do I use this?">
        <p>
          <strong>Email parents their link</strong> — start of season, once rosters are
          uploaded. Each family gets one link covering all of their players, even across
          teams. Sending again is harmless; the link doesn't change.
        </p>
        <p>
          <strong>Nudge families who haven't replied</strong> — a week or so later, to chase
          the stragglers. It skips anyone who already answered, including families who are
          available every day.
        </p>
        <p>
          <strong>Families / Coaches &amp; staff</strong> — pick who you're writing to. The two
          messages differ: families are asked which days their player can't make it, coaches
          are asked for their own dates. Someone who coaches and has a kid on a team is in
          both lists and gets one of each.
        </p>
        <p>
          The coaches' message also carries the <strong>coach</strong> passcode and how to open
          the team view — so it goes only to people you've put under Coaches &amp; staff. The
          families' message never contains it.
        </p>
        <p>
          Both show you the list before anything sends. Anyone with no email on the roster
          can't be emailed — add one with ✉ on the team's Roster.
        </p>
      </SectionHelp>
      <div className="mt-3 inline-flex rounded-lg border border-slate-200 p-0.5">
        {[
          { key: "families", label: "Families" },
          { key: "staff", label: "Coaches & staff" },
        ].map((opt) => (
          <button
            key={opt.key}
            onClick={() => switchAudience(opt.key)}
            className={"px-3 py-1.5 text-sm font-medium rounded-md transition-colors " +
              (audience === opt.key ? "bg-blue-600 text-white" : "text-gray-600 hover:bg-slate-50")}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <p className="mt-2 text-sm text-gray-500">
        {isStaff
          ? `Coaches and team staff get their own message — it asks for their own dates, not a player's, and explains how to open the team view. It contains the coach passcode, so it only goes to people marked Coaches & staff${teamName ? " on " + teamName : " on a roster"}.`
          : `Each family gets one link covering all of their players, even across teams. No password — they just tap it. Families without an email on the roster keep using the team passcode.`}
        {teamName && (
          <> Sends here reach <strong>{teamName}</strong> only.</>
        )}
      </p>

      {error && (
        <div className="mt-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2">
          {error}
        </div>
      )}
      {result && (
        <div className="mt-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm px-3 py-2">
          Sent {result.sent} of {result.attempted}.
          {result.failed && result.failed.length > 0 && (
            <> Couldn’t send to: {result.failed.join(", ")}</>
          )}
        </div>
      )}

      {families === null ? (
        <button
          onClick={preview}
          disabled={busy}
          className="mt-3 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold disabled:opacity-50"
        >
          {busy ? "Loading…" : "See who would be emailed"}
        </button>
      ) : families.length === 0 ? (
        <p className="mt-3 text-sm text-gray-500">
          {isStaff
            ? "No coaches or staff have an email on file yet. Open a team's Roster, add them under Coaches & staff, and click ✉ to add their email."
            : "No roster players have an email yet. Open a team's Roster and click ✉ on a player, or add a Parent Email column to your roster CSV."}
        </p>
      ) : (
        <>
          <div className="mt-3 flex items-center justify-between gap-2">
            <span className="text-sm text-gray-600">
              {families.length}{" "}
              {isStaff
                ? families.length === 1 ? "coach / staff member" : "coaches & staff"
                : families.length === 1 ? "family" : "families"}
            </span>
            <button
              onClick={sendAll}
              disabled={busy}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold disabled:opacity-50"
            >
              {busy ? "Sending…" : "Send to all"}
            </button>
          </div>
          <ul className="mt-2 divide-y divide-slate-100 max-h-64 overflow-y-auto">
            {families.map((f) => (
              <li key={f.email} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-800 truncate">{f.email}</div>
                  <div className="text-xs text-gray-500 truncate">{f.players.join(", ")}</div>
                </div>
                <button
                  onClick={() => sendOne(f.email)}
                  disabled={busy}
                  className="shrink-0 text-sm font-semibold text-blue-600 hover:text-blue-800 disabled:opacity-50"
                >
                  Send
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="mt-5 pt-4 border-t border-slate-200">
        <h4 className="font-bold text-gray-800">Nudge families who haven’t replied</h4>
        <p className="mt-1 text-sm text-gray-500">
          Counts anyone who hasn’t saved yet — including families who are available every
          day, since saving with no dates is still an answer.
        </p>

        {pending === null ? (
          <button
            onClick={checkPending}
            disabled={busy}
            className="mt-3 px-4 py-2 rounded-lg border border-slate-300 text-sm font-semibold text-gray-700 disabled:opacity-50"
          >
            {busy ? "Checking…" : "See who hasn’t replied"}
          </button>
        ) : (
          <>
            <div className="mt-3 text-sm text-gray-600">
              <strong>{pending.responded}</strong> of <strong>{pending.total}</strong> players have
              replied. {pending.families.length} {pending.families.length === 1 ? "family" : "families"} to
              remind.
            </div>
            {pending.noEmail.length > 0 && (
              <div className="mt-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2">
                No email on file, so these can’t be reminded: {pending.noEmail.join(", ")}
              </div>
            )}
            {pending.families.length > 0 && (
              <>
                <ul className="mt-2 divide-y divide-slate-100 max-h-48 overflow-y-auto">
                  {pending.families.map((f) => (
                    <li key={f.email} className="py-2">
                      <div className="text-sm font-medium text-gray-800 truncate">{f.email}</div>
                      <div className="text-xs text-gray-500 truncate">{f.players.join(", ")}</div>
                    </li>
                  ))}
                </ul>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  maxLength={500}
                  placeholder="Optional note to include (e.g. we set the schedule Friday)"
                  className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
                <button
                  onClick={sendReminders}
                  disabled={busy}
                  className="mt-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold disabled:opacity-50"
                >
                  {busy ? "Sending…" : `Remind ${pending.families.length}`}
                </button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
